import {
  AUTO_SCROLL_THRESHOLD_PX,
  getMessagesDiv,
  getScrollBottomBtn,
  getScrollbarEl,
  getScrollbarThumb,
  isNearBottom,
} from './dom';

let autoScrollPinned = true;
let dragStartY = 0;
let dragStartScrollTop = 0;
let isDragging = false;

export function syncAutoScrollPinned(): void {
  autoScrollPinned = isNearBottom(getMessagesDiv());
}

export function isAutoScrollPinned(): boolean {
  return autoScrollPinned;
}

export function scrollToBottom(force = false): void {
  const messagesDiv = getMessagesDiv();
  if (!messagesDiv) return;
  if (!force && !autoScrollPinned) {
    updateScrollbar();
    return;
  }
  requestAnimationFrame(() => {
    messagesDiv.scrollTop = messagesDiv.scrollHeight;
    autoScrollPinned = true;
    updateScrollbar();
  });
}

export function updateScrollbar(): void {
  const scrollbarEl = getScrollbarEl();
  const thumbEl = getScrollbarThumb();
  const messagesDiv = getMessagesDiv();
  const scrollBottomBtn = getScrollBottomBtn();
  if (!scrollbarEl || !thumbEl || !messagesDiv) return;
  const { scrollTop, scrollHeight, clientHeight } = messagesDiv;
  const distanceFromBottom = Math.max(0, scrollHeight - clientHeight - scrollTop);
  autoScrollPinned = distanceFromBottom <= AUTO_SCROLL_THRESHOLD_PX;
  if (scrollBottomBtn) {
    scrollBottomBtn.hidden = distanceFromBottom < AUTO_SCROLL_THRESHOLD_PX;
  }
  if (scrollHeight <= clientHeight) {
    thumbEl.style.display = 'none';
    return;
  }
  thumbEl.style.display = '';
  const trackH = scrollbarEl.clientHeight;
  const thumbH = Math.max(30, trackH * clientHeight / scrollHeight);
  const thumbTop = (scrollTop / (scrollHeight - clientHeight)) * (trackH - thumbH);
  thumbEl.style.height = `${thumbH}px`;
  thumbEl.style.top = `${thumbTop}px`;
}

function onDragStart(e: MouseEvent | TouchEvent): void {
  const messagesDiv = getMessagesDiv();
  const thumbEl = getScrollbarThumb();
  const scrollbarEl = getScrollbarEl();
  isDragging = true;
  dragStartY = e.type === 'touchstart' ? (e as TouchEvent).touches[0].clientY : (e as MouseEvent).clientY;
  dragStartScrollTop = messagesDiv?.scrollTop ?? 0;
  thumbEl?.classList.add('dragging');
  scrollbarEl?.classList.add('active');
  e.preventDefault();
}

function onDragMove(e: MouseEvent | TouchEvent): void {
  if (!isDragging) return;
  const messagesDiv = getMessagesDiv();
  const scrollbarEl = getScrollbarEl();
  if (!messagesDiv || !scrollbarEl) return;
  const clientY = e.type === 'touchmove' ? (e as TouchEvent).touches[0].clientY : (e as MouseEvent).clientY;
  const dy = clientY - dragStartY;
  const { scrollHeight, clientHeight } = messagesDiv;
  const trackH = scrollbarEl.clientHeight;
  const thumbH = Math.max(30, trackH * clientHeight / scrollHeight);
  const ratio = (scrollHeight - clientHeight) / (trackH - thumbH);
  messagesDiv.scrollTop = dragStartScrollTop + dy * ratio;
  e.preventDefault();
}

function onDragEnd(): void {
  if (!isDragging) return;
  const thumbEl = getScrollbarThumb();
  const scrollbarEl = getScrollbarEl();
  isDragging = false;
  thumbEl?.classList.remove('dragging');
  scrollbarEl?.classList.remove('active');
}

export function setupScrollbar(): void {
  const messagesDiv = getMessagesDiv();
  const scrollbarEl = getScrollbarEl();
  const thumbEl = getScrollbarThumb();
  const scrollBottomBtn = getScrollBottomBtn();

  if (!messagesDiv || !scrollbarEl || !thumbEl) return;

  messagesDiv.addEventListener('scroll', () => {
    syncAutoScrollPinned();
    updateScrollbar();
    scrollbarEl.classList.add('scrolling');
    if (messagesDiv.scrollTop <= 80) {
      document.dispatchEvent(new CustomEvent('webcoding-history-top-visible', {
        detail: {
          scrollTop: messagesDiv.scrollTop,
          scrollHeight: messagesDiv.scrollHeight,
          clientHeight: messagesDiv.clientHeight,
        },
      }));
    }
    clearTimeout((scrollbarEl as HTMLElement & { _hideTimer?: number })._hideTimer);
    (scrollbarEl as HTMLElement & { _hideTimer?: number })._hideTimer = window.setTimeout(() => {
      if (!isDragging) scrollbarEl.classList.remove('scrolling');
    }, 1200);
  }, { passive: true });

  new ResizeObserver(updateScrollbar).observe(messagesDiv);

  thumbEl.addEventListener('mousedown', onDragStart);
  thumbEl.addEventListener('touchstart', onDragStart, { passive: false });
  scrollBottomBtn?.addEventListener('click', () => {
    autoScrollPinned = true;
    messagesDiv.scrollTo({ top: messagesDiv.scrollHeight, behavior: 'smooth' });
    updateScrollbar();
  });
  document.addEventListener('mousemove', onDragMove);
  document.addEventListener('touchmove', onDragMove, { passive: false });
  document.addEventListener('mouseup', onDragEnd);
  document.addEventListener('touchend', onDragEnd);

  syncAutoScrollPinned();
  updateScrollbar();
}
